import React, {useMemo, useRef} from "react";
import styled, { css } from "styled-components";
import { ProductSpecification } from "../components/ProductSpecification";
import { ProductApplications } from "../components/ProductApplications";
import { ProductOverview } from "../components/ProductOverview";
import { Content } from "../components/Content";
import { ProductDownload } from "../components/ProductDownload";
import { AccessoryList } from "../components/AccessoryList";
import { ProductReviews } from "../components/ProductReviews";
import Section from "../components/Section";
import Grid from "../components/Grid";
import Container from "../components/Container";
import Spacer from "../components/Spacer";
import ArrowDownIcon from "../components/Icons/ArrowDown";
import { AreasCoveredBannerSmall } from "../components/AreasCoveredBannerSmall";
import { ProductBadges } from "../components/ProductBadges";
import { RelatedCaseStudies } from "../components/RelatedCaseStudies";
import { useStaticQuery, graphql } from "gatsby";
import randomstring from "randomstring";

const TabsStyles = styled.div`
  ${({ ...props }) => css`
    border-radius: 4px;
    overflow: hidden;

    > button {
      border-top: solid 1px #fff;
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      border-radius: 0;
      border-left: solid 8px ${props.theme.colors.primary};
    }
  `}
`;

const TabControlsStyles = styled.section`
  ${({ ...props }) => css`
    display: none;
    @media (min-width: ${props.theme.breakpoints.lg}) {
      display: flex;
    }
  `}
`;

const AccordionButtonStyles = styled.button`
  ${({ ...props }) => css`
    width: 100%;
    border-radius: 0;
    text-align: left;
    padding: 0 24px;
    display: flex;
    line-height: 64px;
    justify-content: space-between;
    align-items: center;

    svg {
      * {
        fill: #fff;
      }
    }
    @media (min-width: ${props.theme.breakpoints.lg}) {
      display: none;
    }
  `}
`;

const TabButtonStyles = styled.button`
  ${({ ...props }) => css`
    @media (min-width: ${props.theme.breakpoints.lg}) {
      background: ${(props) =>
        props.$active ? props.theme.colors.lightGrey : "#bfbfbe"};
      color: ${props.theme.colors.darkGrey};
      min-width: 230px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      margin-right: 4px;

      &:first-child {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-left: solid 8px ${props.theme.colors.primary};
      }

      &:hover,
      &:focus {
        background: ${props.theme.colors.lightGrey};
        outline: none;
        color: ${props.theme.colors.darkGrey};
        cursor: ${(props.$active ? "default" : "pointer")};
      }
    }
  `}
`;

const TabPanelStyles = styled.section`
  ${({ ...props }) => css`
    display: ${(props.$active ? "block" : "none")};
    background-color: ${props.theme.colors.lightGrey};
    padding: 24px;
    border-radius: 4px;

    .product_badges {
      display: none
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      padding: 48px;

      .product_badges {
        display: flex
      }
    }
  `}
`;

const TabPanelHeadingStyles = styled.div`
  ${({ ...props }) => css`
    margin-bottom: 24px;

    @media (min-width: ${props.theme.breakpoints.lg}) {
      margin-bottom: 48px;
    }
  `}
`;

const StyledId = styled.span`
  ${({ ...props }) => css`
    display: block;
    margin: 24px 0;
  `}
`;

const SidebarStyles = styled.div`
  ${({ ...props }) => css`
    > *:first-child {
      margin-top: 0;
    }

    .product_badges {
      margin-top: 48px;
      justify-content: flex-start;
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      .product_badges {
        display: none
      }
    }
  `}
`;

export const ProductTabs = ({ product, page, tabState, setTabState }) => {
  const tabsRef = useRef();

  // Handle Toggle tabs
  function handleTabClick(tab) {
    if (tab === tabState) {
      setTabState("closed");
    } else {
      setTabState(tab);
    }

    if (tabsRef.current) {
      tabsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }

  let data = {
    site: {
      siteMetadata: {
        siteUrl: ''
      }
    }
  }
  if (!process.env.STORYBOOK) {
    data = useStaticQuery(graphql`
      query ProductTabsQuery {
        site {
          siteMetadata {
            siteUrl
          }
        }
      }
    `);
  }

  const rand = useMemo(() => randomstring.generate(5), []);

  return (
    <Section id={`${rand}_product-tabs`} className="product-tabs" ref={tabsRef}>
      <Container>
        <TabControlsStyles>
          <TabButtonStyles
            type="button"
            $active={tabState === "overview"}
            onClick={() => handleTabClick("overview")}
            className="cat-border"
          >
            {product.pageType === "problem"
              ? "Problem details"
              : "Product details"}
          </TabButtonStyles>
          {product.accessoriesDescription || product.accessories ? (
            <TabButtonStyles
              type="button"
              $active={tabState === "accessories"}
              onClick={() => handleTabClick("accessories")}
            >
              Accessories
            </TabButtonStyles>
          ) : (
            ""
          )}
          {(product.pageType === "productOrService" ||
          product.pageType === "componentProduct" ||
          product.pageType === "problem") && (
            <TabButtonStyles
              type="button"
              $active={tabState === "reviews"}
              onClick={() => handleTabClick("reviews")}
            >
              Reviews
            </TabButtonStyles>
          )}
          {product.featuredCaseStudies &&
          product.featuredCaseStudies.length > 0 ? (
            <TabButtonStyles
              type="button"
              $active={tabState === "caseStudies"}
              onClick={() => handleTabClick("caseStudies")}
            >
              Case Studies
            </TabButtonStyles>
          ) : (
            ""
          )}
        </TabControlsStyles>
        <TabsStyles className="cat-border">
          {/* Product details Tab */}
          <>
            <AccordionButtonStyles
              type="button"
              $active={tabState === "overview"}
              onClick={() => handleTabClick("overview")}
              className="cat-bg"
            >
              {product.pageType === "problem"
                ? "Problem details"
                : "Product details"}
              <ArrowDownIcon />
            </AccordionButtonStyles>
            <TabPanelStyles $active={tabState === "overview"}>
              <Grid columnGap="loose">
                <Grid item lg="4" columnGap="loose">
                  <h2>
                    {product.pageType === "problem"
                      ? "Problem details"
                      : "Product details"}
                  </h2>
                </Grid>
                <Grid item lg="8" columnGap="loose">
                  <ProductBadges productMeta={product.productMeta} />
                </Grid>
                <Grid item lg="4" columnGap="loose">
                  <SidebarStyles>
                    {/* Specification */}
                    {product.specification && (
                      <ProductSpecification
                        specification={product.specification}
                        grey={true}
                      />
                    )}
                    {/* Applications */}
                    {product.specification.displayApplicationsOnPage && page.applications.nodes.length > 0 && (
                      <ProductApplications
                        applications={page.applications.nodes}
                        grey={true}
                      />
                    )}
                    {/* PDF Download */}
                    {product.specification?.brochureDownload?.pdf?.localFile?.publicURL && (
                      <ProductDownload
                        label="Download Spec Sheet"
                        url={product.specification.brochureDownload.pdf.localFile.publicURL}
                      />
                    )}
                    {/* Install guide */}
                    {product.specification?.installGuide.pdf?.localFile?.publicURL && (
                      <ProductDownload
                        label="Download Install guide"
                        url={product.specification.installGuide.pdf.localFile.publicURL}
                      />
                    )}
                    {/* PRODUCT ID */}
                    {product.productId && (
                      <StyledId>
                        <strong>Product ID:</strong> {product.productId}
                      </StyledId>
                    )}
                    {product.specification
                      .displayNationwideCoverageBanner && (
                      <AreasCoveredBannerSmall />
                    )}
                    <ProductBadges productMeta={product.productMeta} />
                  </SidebarStyles>
                </Grid>
                <Grid item lg="8" columnGap="loose">
                  {/* Overview components */}
                  <ProductOverview components={product.summary} />
                </Grid>
              </Grid>
            </TabPanelStyles>
          </>
          {/* Accessories Tab */}
          {(product.accessoriesDescription || product.accessories) && (
            <>
              <AccordionButtonStyles
                type="button"
                $active={tabState === "accessories"}
                onClick={() => handleTabClick("accessories")}
                className="cat-bg"
              >
                Accessories
                <ArrowDownIcon />
              </AccordionButtonStyles>
              <TabPanelStyles $active={tabState === "accessories"}>
                <TabPanelHeadingStyles>
                  <h2>Accessories</h2>
                </TabPanelHeadingStyles>
                <Spacer>
                  {product.accessoriesDescription && (
                    <Content content={product.accessoriesDescription} />
                  )}
                  {product.accessories &&
                    product.accessories.length > 0 && (
                      <AccessoryList accessories={product.accessories} />
                    )}
                </Spacer>
              </TabPanelStyles>
            </>
          )}
          {/* Reviews Tab */}
          {(product.pageType === "productOrService" ||
          product.pageType === "componentProduct" ||
          product.pageType === "problem") && (
            <>
              <AccordionButtonStyles
                type="button"
                $active={tabState === "reviews"}
                onClick={() => handleTabClick("reviews")}
                className="cat-bg"
              >
                Reviews
                <ArrowDownIcon />
              </AccordionButtonStyles>
              <TabPanelStyles $active={tabState === "reviews"}>
                <TabPanelHeadingStyles>
                  <h2>
                    Reviews
                  </h2>
                </TabPanelHeadingStyles>
                <ProductReviews
                  productId={page.id}
                  name={page.title}
                  source={page.uri}
                  isGreyBg={true}
                  outOf={true}
                />
              </TabPanelStyles>
            </>
          )}
          {/* Case studies Tab */}
          {product.featuredCaseStudies && product.featuredCaseStudies.length > 0 && (
            <>
              <AccordionButtonStyles
                type="button"
                $active={tabState === "caseStudies"}
                onClick={() => handleTabClick("caseStudies")}
                className="cat-bg"
              >
                Case studies
                <ArrowDownIcon />
              </AccordionButtonStyles>
              <TabPanelStyles $active={tabState === "caseStudies"}>
                <TabPanelHeadingStyles>
                  <h2>
                    Case studies
                  </h2>
                </TabPanelHeadingStyles>
                <RelatedCaseStudies
                  caseStudies={product.featuredCaseStudies}
                  grid
                />
              </TabPanelStyles>
            </>
          )}
        </TabsStyles>
      </Container>
    </Section>
  )
}